import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../api";
import { Icon, Spinner } from "../../components/ui";

const PROCESSES = [
  { key: "SUBMISSION", label: "Submission" },
  { key: "END_PROCESS", label: "End Process" },
  { key: "MODIFICATION", label: "Modification" },
  { key: "MORE_INFO", label: "More Info" },
];

// Business "today" rolls per shift: NIGHT/unset at 8:30 AM IST (= 03:00 UTC), MORNING at 11:00 PM IST (= 17:30 UTC).
// So after 11 PM the Morning view shows a fresh next day (zero), and after 8:30 AM the Night view does.
function nightTodayISO() { return new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10); }
function morningTodayISO() { return new Date(Date.now() + 6.5 * 3600 * 1000).toISOString().slice(0, 10); }
function shiftTodayISO(shift) { return shift === "MORNING" ? morningTodayISO() : nightTodayISO(); }
function prettyDate(iso, shift) {
  if (iso === shiftTodayISO(shift)) return "Today";
  try { return new Date(iso + "T00:00:00").toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

// Shift filter. Unset shift = night timing, so it falls under "Night".
const SHIFTS = [["ALL", "All shifts"], ["NIGHT", "Night"], ["MORNING", "Morning"]];
function matchShift(sel, shift) {
  if (sel === "ALL") return true;
  const s = (shift || "").toUpperCase();
  if (sel === "MORNING") return s === "MORNING";
  return s !== "MORNING";   // Night = NIGHT or unset
}

export function Allocations() {
  const [shift, setShift] = useState("ALL");
  const [date, setDate] = useState(shiftTodayISO("ALL"));
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async (silent) => {
    if (!silent) setRows(null);
    setErr("");
    try { setRows(await api.dailyAllocations(date, date, silent ? { silent: true } : undefined)); }   // single day = from == to
    catch (e) { setErr(e.message); if (!silent) setRows([]); }
  }, [date]);
  useEffect(() => { load(); }, [load]);
  // Live: silently re-pull every 20s so Assigned / Completed stay current as records are assigned and worked.
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) load(true); }, 20000);
    return () => clearInterval(id);
  }, [load]);

  // Switching shift jumps to that shift's current business day (night rolls 8:30 AM, morning rolls 11 PM).
  const onShift = (s) => { setShift(s); setDate(shiftTodayISO(s)); };

  const byUser = new Map();
  (rows || []).filter((r) => matchShift(shift, r.shift)).forEach((r) => {
    if (!byUser.has(r.user)) byUser.set(r.user, {});
    const rec = byUser.get(r.user);
    if (!rec[r.sheet]) rec[r.sheet] = { a: 0, c: 0, l: 0 };
    rec[r.sheet].a += Number(r.assigned || 0);
    rec[r.sheet].c += Number(r.completed || 0);
    rec[r.sheet].l += Number(r.left || 0);
  });
  const users = [...byUser.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  const cell = (v) => (v ? v : <span className="alloc-zero">·</span>);

  return (
    <div className="jr-page">
      <div className="alloc-topbar">
        <h2 style={{ margin: 0 }}>Allocations</h2>
        <label className="alloc-date"><span>Date</span>
          <input type="date" className="inp" value={date} max={shiftTodayISO(shift)} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="alloc-date"><span>Shift</span>
          <select className="inp" value={shift} onChange={(e) => onShift(e.target.value)}>
            {SHIFTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <span className="jr-spacer" />
      </div>

      {err && <div className="import-err"><Icon.x width="15" height="15" /><span>{err}</span></div>}

      <div className="card cmd-card">
        <div className="alloc-day-head">{prettyDate(date, shift)} — assigned / completed / left, by process</div>
        <div className="table-wrap"><table className="tbl cmd-table alloc-proc-table">
          <thead>
            <tr>
              <th rowSpan={2} style={{ verticalAlign: "bottom" }}>User</th>
              {PROCESSES.map((p) => <th key={p.key} colSpan={3} style={{ textAlign: "center", borderLeft: "2px solid #e5e7eb" }}>{p.label}</th>)}
            </tr>
            <tr>
              {PROCESSES.map((p) => (
                <React.Fragment key={p.key}>
                  <th style={{ textAlign: "center", borderLeft: "2px solid #e5e7eb", fontSize: 11 }}>Assigned</th>
                  <th style={{ textAlign: "center", color: "#047857", fontSize: 11 }}>Completed</th>
                  <th style={{ textAlign: "center", color: "#b45309", fontSize: 11 }}>Left</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              <tr><td colSpan={1 + PROCESSES.length * 3}><div className="empty"><Spinner /></div></td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={1 + PROCESSES.length * 3} style={{ textAlign: "center", color: "#94a3b8", padding: "26px 0" }}>No records allocated on {prettyDate(date, shift).toLowerCase()}.</td></tr>
            ) : users.map(([user, procs]) => (
              <tr key={user}>
                <td className="cell-strong">{user}</td>
                {PROCESSES.map((p) => { const v = procs[p.key] || { a: 0, c: 0, l: 0 }; const left = v.l; return (
                  <React.Fragment key={p.key}>
                    <td style={{ textAlign: "center", fontWeight: 700, borderLeft: "2px solid #e5e7eb" }}>{cell(v.a)}</td>
                    <td style={{ textAlign: "center", color: "#047857", fontWeight: 700 }}>{cell(v.c)}</td>
                    <td style={{ textAlign: "center", color: "#b45309", fontWeight: 700 }}>{cell(left)}</td>
                  </React.Fragment>
                ); })}
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}